import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { HiArrowLeft } from 'react-icons/hi'

import {
  Box as DarkBox,
  IconButton as DarkIconButton,
  Skeleton,
} from '@hivewatch/dark'

import { StandardOperatingProcedureQuery } from '@/graphql/generated/operations'
import { formatDate } from '@/utils/formatDateTime'
import { formatFullName } from '@/utils/formats'

type SOPType =
  | StandardOperatingProcedureQuery['standardOperatingProcedure']
  | undefined

interface SOPDrawerHeaderIProps {
  sop: SOPType
  isLoading: boolean
  onClose: () => void
}

export const SOPDrawerHeader = ({
  sop,
  isLoading,
  onClose,
}: SOPDrawerHeaderIProps) => {
  const mode = useLightMode()
  const { t } = useTranslation()

  const creatorFullName = sop?.creator ? formatFullName(sop.creator) : ''

  const prefix = mode === 'light' ? 'light.' : ''
  return (
    <DarkBox
      alignItems='center'
      display='flex'
      gap='12px'
      height='64px'
      pb='16px'
      pt='16px'
      px='24px'
    >
      <DarkIconButton
        _active={{ bg: 'transparent' }}
        aria-label='Back button'
        colorScheme={mode}
        dimension={36}
        hoverBgColor='transparent'
        icon={<HiArrowLeft size={18} />}
        onClick={onClose}
      />
      <DarkBox
        as='span'
        color={`${prefix}text.primary`}
        flex='1'
        fontFamily="'Soehne Kraftig', 'Söhne Kräftig', sans-serif"
        fontSize='20px'
        fontWeight='500'
        lineHeight='28px'
        minWidth={0}
        overflow='hidden'
        textOverflow='ellipsis'
        whiteSpace='nowrap'
      >
        {isLoading && !sop ? (
          <Skeleton bg={`${prefix}bg.skeleton`} h='20px' w='120px' />
        ) : (
          sop?.name
        )}
      </DarkBox>
      <DarkBox alignItems='center' display='flex' gap='16px'>
        <DarkInfoBox
          isLoading={isLoading}
          label={t('incidents:drawer_sop_creator_label')}
          prefix={prefix}
          text={creatorFullName}
        />
        <DarkBox
          bg={`${prefix}border.subtle`}
          flexShrink={0}
          height='32px'
          width='1px'
        />
        <DarkInfoBox
          isLoading={isLoading}
          label={t('incidents:drawer_sop_date_created_label')}
          prefix={prefix}
          text={formatDate(sop?.createdAt)}
        />
      </DarkBox>
    </DarkBox>
  )
}

const DarkInfoBox = ({
  label,
  text,
  isLoading,
  prefix,
}: {
  label: string
  text: string
  isLoading: boolean
  prefix: string
}) => (
  <DarkBox display='flex' flexDirection='column' gap='2px'>
    {isLoading && !text ? (
      <Skeleton bg={`${prefix}bg.skeleton`} h='16px' w='80px' />
    ) : (
      <>
        <DarkBox
          as='span'
          color={`${prefix}text.tertiary`}
          fontFamily="'Soehne', sans-serif"
          fontSize='11px'
          fontWeight='400'
          lineHeight='14px'
        >
          {label}
        </DarkBox>
        <DarkBox
          as='span'
          color={`${prefix}text.primary`}
          fontFamily="'Soehne', sans-serif"
          fontSize='14px'
          fontWeight='500'
          lineHeight='18px'
          maxWidth='170px'
          overflow='hidden'
          textOverflow='ellipsis'
          whiteSpace='nowrap'
        >
          {text}
        </DarkBox>
      </>
    )}
  </DarkBox>
)
